import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { formatApiError } from '../lib/api';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import Logo from '../components/Logo';

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate('/');
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
         style={{ background: 'radial-gradient(ellipse at top, #1e1b4b 0%, #0B1121 60%)' }}>
      {/* Background grid */}
      <div className="absolute inset-0 opacity-[0.04]"
           style={{
             backgroundImage: 'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)',
             backgroundSize: '60px 60px',
           }} />

      <div className="relative w-full max-w-md fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex justify-center mb-2">
            <Logo size="xl" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight mt-4" style={{
            background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 50%, #D97706 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}>
            MB 968 GROUP
          </h1>
          <div className="flex items-center justify-center gap-3 mt-3">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-700/60" />
            <p className="text-[11px] uppercase tracking-[0.32em] text-slate-400 font-medium">
              虚 拟 卡 业 务
            </p>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-700/60" />
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="login-form"
          className="card-base p-7 space-y-5 backdrop-blur-md"
          style={{ background: 'rgba(23, 33, 54, 0.85)' }}
        >
          <div>
            <label className="label-tiny block mb-2">邮箱</label>
            <input
              data-testid="login-email"
              type="email"
              autoComplete="email"
              required
              className="input-base"
              placeholder="admin@vcard.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="label-tiny block mb-2">密码</label>
            <div className="relative">
              <input
                data-testid="login-password"
                type={showPass ? 'text' : 'password'}
                autoComplete="current-password"
                required
                className="input-base pr-12"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {showPass ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="btn-primary w-full py-3"
          >
            {loading ? '登录中…' : '登 录'}
          </button>

          <p className="text-xs text-center text-slate-500 pt-2">
            内部管理系统 · 仅限授权员工使用
          </p>
        </form>
      </div>
    </div>
  );
}
